import { apiClient } from '../../core/api';

/**
 * The store-manager data plane (#T1).
 *
 * Every consumer — Task Center (#22/#23), the Workspace status line (#4), "tasks today" (#5) and
 * the store manager's ledger (#5.2 §3) — reads THESE endpoints. PRD #5.2 requires the ledger count
 * to equal the task-centre count, which only holds if nobody derives their own numbers.
 */

/**
 * PRD #22's task STAGES, as the backend stores them.
 *
 * A task is one deliverable moving through stages — #22's routing table gives one piece of content
 * three of them, each routing somewhere different: 生成中 → Marketing Hub Draft, 待发布 → Approval
 * Center, 发布成功 → Material Hub. `AWAITING_APPROVAL` is that middle stage, and it is why a row's
 * View destination changes over its life instead of being fixed at creation.
 *
 * The backend's internal `PROVISIONAL` / `DISCARDED` are deliberately absent: they are filtered out
 * server-side and can never reach this type.
 */
export type AgentTaskStatus =
  | 'QUEUED'
  | 'RUNNING'
  // 待处理。BE-3 renamed AWAITING_APPROVAL → NEEDS_ACTION (审批语系废除, Infra 1.5 #16/#19);
  // rows written by an old pod mid-rollout may still carry the legacy spelling, so both stay
  // in the union and every consumer must treat them as the same stage.
  | 'NEEDS_ACTION'
  | 'AWAITING_APPROVAL'
  | 'SUCCEEDED'
  | 'FAILED';
export type AgentTaskTrigger = 'manual' | 'scheduled' | 'event';
/** Which decision a waiting row is waiting for — see `AgentTask.actionKind`. */
export type AgentTaskActionKind = 'approve' | 'alert' | 'takeover' | 'kb_confirm';

export interface AgentTaskStep {
  seq: number;
  type: string;
  direction?: string;
  toolName?: string | null;
  summary?: string | null;
  at?: string | null;
}

/**
 * One recorded phase of a task, as the server froze it.
 *
 * Two producers write this, and the difference matters when reading a row. An agent walking a
 * skills-hub task graph reports nodes as it enters them; a SCHEDULED deliverable (the weekly report,
 * the sourcing report) walks no graph and instead records the phases its batch actually went
 * through. Both arrive here in the same shape because both answer one question — what happened, in
 * order — and the Steps checklist asks nothing else.
 */
export interface AgentTaskPhase {
  node?: string | null;
  label?: string | null;
  index?: number | null;
  at?: string | null;
}

export interface AgentTask {
  id: string;
  agentType: string;
  taskType?: string | null;
  triggerType: AgentTaskTrigger;
  status: AgentTaskStatus;
  stepDone: number;
  /**
   * Null while RUNNING: how many tools a turn will call is unknowable until it ends. Kept alongside
   * `progressPercent` so the bar can show a percentage without anyone forgetting the real total
   * isn't known yet.
   */
  stepTotal?: number | null;
  /**
   * Progress percentage (PRD #4). Server-derived from the step count against a FIXED per-task-type
   * estimate, which is what makes it safe to draw: a fixed denominator can only increase, where a
   * discovered one would make the bar jump backwards. Capped below 100 until the turn really ends,
   * so it never claims completion early.
   */
  progressPercent: number;
  /**
   * NOT displayed. PRD V1.28 ③ removed the task centre's Credit used column and moved #21 (the
   * Credit usage bar) to Trash entirely, so nothing renders this.
   *
   * Kept on the type because the backend still records it and it is genuinely useful for support —
   * dropping the field would mean losing the data, where dropping the column only means not showing
   * a number product decided not to show.
   */
  creditCost?: number | null;
  /** 商家读的行名(后端 displayName:任务定义名 → 交付物名 → taskType 兜底)。
   *  "Publish: 《标题》"/"Regenerate: 《标题》"都在这一字段 —— 渲染必须优先它,
   *  taskType 静态表只是没有名字时的兜底。 */
  name?: string | null;
  resultRef?: string | null;
  conversationId?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  /**
   * RAW technical text — NEVER render it (#5: 失败一律显示商家能懂的原因, 不暴露技术用语).
   * Kept for support tooling only; the merchant-facing sentence is {@link failureHeadline}.
   */
  errorMessage?: string | null;
  /** 商家能读的失败一句话, from the copy table (BE-13). Render THIS, never errorMessage. */
  failureHeadline?: string | null;
  /** The consequence line under the headline, when the copy table has one. */
  failureConsequence?: string | null;
  /** Classification for observability; never rendered, never branched on client-side. */
  failureKind?: string | null;
  failureCode?: string | null;
  /**
   * Required channels out, an optional one missing → the deliverable stands (#14). Rendered as a
   * 「Done · degraded」 badge on a SUCCEEDED row; NOT a failure and never a red box.
   */
  degraded?: boolean;
  /** Some required channel failed while others posted — FAILED with a consequence line. */
  partial?: boolean;
  /** Server-computed view destination; the local #22 table wins, this is the fallback. */
  viewRoute?: string | null;
  /** The recovery card this task is stuck on — a failed row's click opens the panel AT this card. */
  cardId?: string | null;
  /**
   * The batch this row is one item of, or null for a top-level task (PRD #2/#4). Children never
   * render as top-level rows — they fold into their parent's expansion, one line per product.
   */
  parentRef?: string | null;
  /** The item's business id inside its batch (variant number / SKU) — the sub-row's label fallback. */
  itemRef?: string | null;
  /**
   * [Infra 1.5 #1] 参与这条任务的其他 agent 的 `agentType`，<b>不含主责</b>（主责是 {@link agentType}）。
   *
   * <p>后端只给类型，不给「参与了什么」—— 直接交给 `agentIdentity()` 取名字。
   * null / 空数组 = 独立任务。
   */
  participants?: string[] | null;
  /** [Infra 1.5 #18] 会话名。第二层分组（agent → 会话）的标题。 */
  conversationTitle?: string | null;
  /** [Infra 1.5 #18] 会话最后活动时间，会话按它倒序。 */
  conversationLastActivityAt?: string | null;
  /**
   * 降级行的那一句：少的是哪一块（PRD #14 / UI 清单行5）。
   *
   * 只有降级行有。降级是 SUCCEEDED —— 东西发出去了，只是少了一块可选的，所以它不该顶着红框，
   * `failureHeadline` 对它是 null。但「Degraded」本身不告诉商家任何可做的事，而缺文案和缺图
   * 要去的地方不一样，所以后端按行给出各自的句子。
   */
  degradedNote?: string | null;
  /**
   * 这一行现在正在做的那一步，已经是商家看得懂的一句话（「Enhancing photo 2」）。
   *
   * ⚠️ **只在 RUNNING 时读。** 任务停下来之后服务端还会往同一列写内部标记
   * （`awaiting_review`、`degraded:<marker>`，后者是它自己推降级文案用的），
   * 照着渲染的话商品行上会冒出 `degraded:empty_image_slot` 这种东西。
   */
  currentStep?: string | null;
  retryCount: number;
  /**
   * [Infra 1.5 #19] WHICH decision a waiting row is waiting for; null on every row that is not
   * waiting on the merchant.
   *
   * <p>Replaces the old `needsApproval` boolean (PRD #19 「同步删除」). With the approval centre gone
   * a yes/no could only say "is this a draft", and every other pending row — Oscar's alerts, Anna's
   * waiting conversations — had to be guessed at. The server derives it from the waiting state, so
   * a published draft does not keep saying `approve`.
   *
   * - `approve` — a Maya draft; opens the draft.
   * - `alert` — Oscar's Action Center (the daily digest, a critical alert).
   * - `takeover` — conversations waiting for a human; opens Anna Chat.
   * - `kb_confirm` — declared server-side, no producer this cycle (CS PRD V1.7 → 2.0).
   */
  actionKind?: AgentTaskActionKind | null;
  /**
   * The merchant has not seen this task in its current state (YEP-803, server-side).
   *
   * <p>Not "have they opened it" but "have they seen it LIKE THIS": a task read while running and
   * later failed is unread again. The server holds the state they last saw and derives this per
   * request — see chatbot-api's `AgentTaskReadRepository#findUnreadTaskIds`.
   *
   * <p>Absent means the backend did not answer the question — treated as READ, so a client running
   * against an older backend draws no dots rather than lighting every row.
   */
  unread?: boolean;
  /** Only present on the detail endpoint. */
  steps?: AgentTaskStep[] | null;
  /**
   * The server's frozen phase walk — present for a scheduled deliverable, and for an agent turn
   * that entered a task graph.
   *
   * The backend has always sent this; nothing read it, so the Steps checklist was empty for every
   * scheduled row (dev: `alert_digest`, `operation_report`, `sourcing_report` all showed "No steps
   * recorded for this run"). Distinct from `steps`, which is the WS frame trail and only exists for
   * work that ran inside a conversation — a batch job has no frames at all.
   */
  taskSteps?: AgentTaskPhase[] | null;
}

/**
 * FAILED > AWAITING_APPROVAL > RUNNING > RECENTLY_DONE > IDLE, resolved server-side (#4).
 *
 * PRD #4 rev 2026-08-11 split the old `NEEDS_ATTENTION` into FAILED and AWAITING_APPROVAL: one is
 * "the system broke", the other "the system is waiting for you", and the card routes them to
 * different pages. `NEEDS_ATTENTION` is kept in the union only so a client running against an
 * older backend still type-checks; nothing emits it any more.
 */
export type AgentState =
  | 'FAILED'
  | 'AWAITING_APPROVAL'
  | 'RUNNING'
  | 'RECENTLY_DONE'
  | 'IDLE'
  | 'NEEDS_ATTENTION';

export interface AgentStatus {
  agentType: string;
  state: AgentState;
  taskId?: string | null;
  /**
   * The conversation the task ran in — where a red dot has to land (#6).
   *
   * Null for a SCHEDULED deliverable (weekly report, sourcing scan), which ran in no conversation at
   * all: fall back to the agent's default page rather than navigating nowhere.
   */
  conversationId?: string | null;
  taskType?: string | null;
  triggerType?: AgentTaskTrigger | null;
  stepDone?: number | null;
  stepTotal?: number | null;
  /** See AgentTask.progressPercent. */
  progressPercent?: number | null;
  startedAt?: string | null;
  /** Set once the task ended — PRD #4 words the finished states as "… · <time>". */
  endedAt?: string | null;
  errorMessage?: string | null;
  tasksToday: number;
  /**
   * PRD #19 —— 这个 agent 现在有多少件在等商家（`NEEDS_ACTION` ＋ 它替换掉的遗留
   * `AWAITING_APPROVAL`）。任务中心「待处理」页签筛的就是这两个状态，所以这个数字与它跳过去
   * 看到的那一屏是同一个集合。
   *
   * <p><b>可选是故意的，不是没写全。</b>本仓的 dev 会先于 chatbot-api 促到 test / main，
   * 那段时间里后端的响应上还没有这个字段。调用方一律 `?? 0` 或回落到审批队列，
   * 别让它变成 NaN。
   *
   * <p>不是「今天的」，也不是列表的一个切片：上周二提出来的事今天仍然在等。后端是 SQL 计数，
   * 不是翻页数出来的 —— 翻页会和商家正看着的那个数字悄悄不一致。
   */
  pendingCount?: number;
}

export interface AgentStatusRoster {
  agents: AgentStatus[];
  tasksTodayTotal: number;
  /** The IANA zone the day boundary was computed in — label the count with it, don't imply UTC. */
  timezone?: string | null;
}

export interface AgentTaskFilters {
  agent?: string;
  status?: AgentTaskStatus;
  trigger?: AgentTaskTrigger;
  /** ISO-8601. The backend rejects anything else with a message naming the format. */
  from?: string;
  to?: string;
}

const base = (userId: string) => `/digital-staff/store-manager/tasks/${encodeURIComponent(userId)}`;

/**
 * An HTTP failure that `apiClient` resolved instead of rejecting.
 *
 * <p>Non-401 errors come back as a 200 carrying `{errorCode, errorMessage}` (see
 * `core/api.ts`), so a caller that only checks the shape of the body cannot tell an outage from an
 * empty result. For a LIST that difference is the whole answer — and for the unread baseline it is
 * the difference between "this merchant has no tasks" and "we could not ask", which would seed an
 * empty registry and then declare the merchant's entire history unread.
 */
function isResolvedApiError(body: unknown): boolean {
  return (
    !!body && typeof body === 'object' && 'errorCode' in body && 'errorMessage' in body
  );
}

export async function listAgentTasks(
  userId: string,
  filters: AgentTaskFilters = {}
): Promise<AgentTask[]> {
  const response = await apiClient.chatbotGet<AgentTask[]>(
    base(userId),
    filters as Record<string, unknown>
  );
  if (isResolvedApiError(response.data)) {
    const body = response.data as unknown as { errorMessage?: string };
    throw new Error(body.errorMessage || 'Could not load tasks');
  }
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * The merchant expanded this task row, i.e. read it (YEP-803).
 *
 * <p>Fire and forget: the server is silent on an id this merchant does not own, and a lost report
 * costs a dot that clears on the next expand — never a broken page.
 */
export async function markAgentTaskRead(userId: string, taskId: string): Promise<void> {
  if (!userId || !taskId) return;
  const resp = await apiClient.chatbotPost(`${base(userId)}/${encodeURIComponent(taskId)}/read`);
  // Same resolved-error trap as the reads: a 500 comes back as a 200 carrying
  // {errorCode, errorMessage}. Reported as success it would leave this tab's local note suppressing
  // the dot forever — the note is only dropped for rows the server calls read, and this one never
  // will be — while the task stays unread on every other surface.
  // `typeof` first: this endpoint answers a bare `true`/`false`, and `in` throws on a primitive.
  const body = resp.data as unknown;
  if (isResolvedApiError(body)) {
    throw new Error((body as { errorMessage?: string }).errorMessage || 'Could not mark the task read');
  }
}

export async function getAgentTask(userId: string, taskId: string): Promise<AgentTask> {
  const response = await apiClient.chatbotGet<AgentTask>(
    `${base(userId)}/${encodeURIComponent(taskId)}`
  );
  return response.data;
}

/**
 * Delete a task (Infra 1.5 #5 / V0.9②): the row's grey link and the store-manager card's
 * 「Delete task」 are ONE action, named Delete everywhere, no second confirmation. Logical delete
 * server-side (status='DELETED'); a second press is a no-op, not an error. The row leaves the
 * centre and nothing in the store changes.
 *
 * <p>Replaces retryAgentTask: V1.0②⑦ removed the task centre's single-row Retry — failed rows are
 * read-only, and recovery lives entirely in the store-manager panel.
 */
export async function deleteAgentTask(userId: string, taskId: string): Promise<void> {
  await apiClient.chatbotPost(`${base(userId)}/${encodeURIComponent(taskId)}/delete`, {});
}

/** Per-agent, per-status totals. Grouped in SQL so they can't disagree with the list (#5.2). */
export async function getAgentTaskCounts(
  userId: string,
  range: { from?: string; to?: string } = {}
): Promise<Record<string, Record<string, number>>> {
  const response = await apiClient.chatbotGet<Record<string, Record<string, number>>>(
    `${base(userId)}/counts`,
    range as Record<string, unknown>
  );
  return response.data ?? {};
}

/** PRD #6 (2026-08-27): a regenerate leaves a "Regenerate: 《title》" record in the Task
 *  Center. Complete-on-arrival on the backend — fire-and-forget here; losing the trace
 *  must never block the regenerate itself. */
export async function recordRegenerateTrace(
  userId: string,
  title: string,
  source: string
): Promise<void> {
  await apiClient.chatbotPost(`${base(userId)}/regenerate-trace`, undefined, {
    params: { title, source },
  });
}

/** #4 status line + #5 "tasks today", in one call. Derived from agent_tasks, not the runtime. */
export async function getAgentStatusRoster(userId: string): Promise<AgentStatusRoster> {
  const response = await apiClient.chatbotGet<AgentStatusRoster>(
    `/digital-staff/store-manager/agents/status/${encodeURIComponent(userId)}`
  );
  return response.data ?? { agents: [], tasksTodayTotal: 0, timezone: null };
}

/** Display helpers — kept next to the types so every consumer words progress the same way. */

/**
 * The percentage PRD #4 asks for. Server-derived — never recomputed here, or the task centre and the
 * store manager's ledger could show different numbers for the same task.
 */
export function progressPercent(task: Pick<AgentTask, 'progressPercent'>): number {
  return Math.max(0, Math.min(100, task.progressPercent ?? 0));
}

/**
 * Secondary step text next to the bar ("step 3", or "4 steps" once finished). Kept because the
 * percentage is an estimate against a fixed per-type total, while the step count is what actually
 * happened — showing both means the bar reads well without overstating what is known.
 */
/**
 * The status line's own wording (#4). Separate from {@link stepLabel} because the roster carries a
 * `state`, not a task `status` — and because the line is one short phrase, where the task centre has a
 * bar plus a secondary label.
 *
 * <p>RUNNING deliberately shows the step COUNT and no total: the backend leaves `stepTotal` null while a
 * turn is live, because how many tools it will call is unknowable until it ends. "step 3 of ?" is worse
 * than "step 3".
 */
export function agentStateLabel(status: Pick<AgentStatus, 'state' | 'stepDone'>): string {
  switch (status.state) {
    case 'RUNNING':
      return `Working · step ${status.stepDone ?? 0}`;
    case 'FAILED':
      return 'Failed';
    case 'AWAITING_APPROVAL':
      return 'Needs your approval';
    // Pre-split backends only. Kept so an old response still reads sensibly.
    case 'NEEDS_ATTENTION':
      return 'Needs attention';
    case 'RECENTLY_DONE':
      return 'Just finished';
    case 'IDLE':
    default:
      return 'Idle';
  }
}

export function stepLabel(task: Pick<AgentTask, 'status' | 'stepDone' | 'stepTotal'>): string {
  if (task.status === 'QUEUED') return '—';
  if (task.stepTotal != null) return `${task.stepTotal} step${task.stepTotal === 1 ? '' : 's'}`;
  return `step ${task.stepDone ?? 0}`;
}

/**
 * PRD #22's stage wording for a row in the task list.
 *
 * Deliberately phrased as stages of a deliverable rather than states of a job, because that is what
 * they are: "Ready to publish" is a thing the merchant can act on, where "Succeeded" would read as
 * finished and leave them with no reason to look.
 */
export function taskStageLabel(status: AgentTaskStatus): string {
  switch (status) {
    case 'QUEUED':
      return 'Queued';
    case 'RUNNING':
      return 'Generating';
    case 'AWAITING_APPROVAL':
      return 'Ready to publish';
    case 'SUCCEEDED':
      return 'Published';
    case 'FAILED':
      return 'Failed';
    default:
      return status;
  }
}
